import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import {
    EmptyValue,
    MutedValue,
    RecordCard,
    RecordTableToolbar,
    recordTablePagination,
} from '@client/components/_ui/display/RecordPrimitives';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { createColumnHelper, PrismaTable, TableEmptyState, usePrismaTable } from '@client/hooks/table-v2';
import { type RouterInputs, type RouterOutputs, trpc } from '@client/trpc/client';
import { ORG_CONFIG_PERMISSIONS } from '@shared/permissions/permissions';
import { createFileRoute } from '@tanstack/react-router';
import { Input, Tag } from 'antd';
import { Info } from 'lucide-react';
import { useState } from 'react';
import { CLIENT_DETAILS_ROUTE, useNetworkRecord } from './network-record-data';

export const Route = createFileRoute(
    '/console/$orgId/configuration/vendor-management/repair-networks/details/$id/clients',
)({
    staticData: {
        // Fleet client rows are fleet-module data; without the module the tab is hidden.
        modules: ['fleet'],
        permissions: [...ORG_CONFIG_PERMISSIONS],
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <ClientsTab />
        </PageController>
    );
}

type ClientsInput = RouterInputs['repairNetwork']['listRepairNetworkClients'];
type ClientsQueryParams = Omit<ClientsInput, 'repairNetworkId'>;
type ClientRow = RouterOutputs['repairNetwork']['listRepairNetworkClients']['rows'][number];

// The client list has no sortable columns; the sort key type is nominal.
const columnHelper = createColumnHelper<ClientRow, { name: 'asc' | 'desc' }>();

/** The clients drawing from this network, by their own selection or through the default. */
function ClientsTab() {
    const orgId = useOrgId();
    const navigate = useNavigate();
    const { id } = Route.useParams();
    const { data: network } = useNetworkRecord(id);
    const [params, setParams] = useState<ClientsQueryParams>({ limit: 20, offset: 0 });
    const [searchText, setSearchText] = useState('');

    const clientsQuery = trpc.repairNetwork.listRepairNetworkClients.useQuery({ ...params, repairNetworkId: id });

    const tableProps = usePrismaTable({
        rowKey: 'id',
        data: clientsQuery.data?.rows || [],
        total: clientsQuery.data?.total || 0,
        loading: clientsQuery.isLoading,
        queryParams: params,
        onParamsChange: setParams,
        pagination: recordTablePagination,
        emptyContent: (
            <TableEmptyState
                hasActiveFilters={!!params.search}
                noRecordsTitle="No clients yet"
                noRecordsDescription="A client chooses a network from its record; the organisation default serves everyone else."
                noMatchesTitle="No clients match"
                noMatchesDescription="Try another name or client number, or clear the search."
            />
        ),
        columns: [
            columnHelper.accessor('name', {
                id: 'name',
                header: 'Client',
                cell: ({ row }) => <span className="font-semibold">{row.name}</span>,
            }),
            columnHelper.accessor('clientNo', {
                id: 'clientNo',
                header: 'Client no',
                width: 120,
                cell: ({ row }) => <span className="text-subtle-foreground tabular-nums">{row.clientNo}</span>,
            }),
            columnHelper.display({
                id: 'consolidator',
                header: 'Consolidator',
                cell: ({ row }) => row.Parent?.name || <EmptyValue>—</EmptyValue>,
            }),
            columnHelper.accessor('source', {
                id: 'source',
                header: 'How',
                width: 170,
                cell: ({ row }) =>
                    row.source === 'client' ? (
                        <Tag variant="outlined" className="m-0">
                            Own selection
                        </Tag>
                    ) : (
                        <MutedValue>Organisation default</MutedValue>
                    ),
            }),
            columnHelper.actions({
                width: 40,
                cell: ({ row }) => (
                    <div onClick={(event) => event.stopPropagation()}>
                        <Button.OpenInNewTab to={CLIENT_DETAILS_ROUTE} params={{ orgId, id: row.id }} />
                    </div>
                ),
            }),
        ],
    });

    return (
        <RecordCard
            padded={false}
            footer={
                <>
                    <Info className="size-3.5 shrink-0" />
                    <span>A client's network is changed from its record in the fleet client database.</span>
                </>
            }
        >
            <RecordTableToolbar>
                <Input.Search
                    placeholder="Search clients"
                    allowClear
                    className="w-72"
                    value={searchText}
                    onChange={(event) => setSearchText(event.target.value)}
                    onSearch={(value) =>
                        setParams((prev) => ({ ...prev, offset: 0, search: value.trim() || undefined }))
                    }
                />
                <span className="ml-auto text-subtle-foreground text-xs">
                    {network
                        ? `${network.clientCount} ${network.clientCount === 1 ? 'client' : 'clients'}${network.defaultClientCount ? ` · ${network.defaultClientCount} by organisation default` : ''}`
                        : ''}
                </span>
            </RecordTableToolbar>
            {clientsQuery.error ? (
                <div className="p-4">
                    <InlineQueryError compact error={clientsQuery.error} onRetry={() => void clientsQuery.refetch()} />
                </div>
            ) : (
                <PrismaTable
                    {...tableProps}
                    onRow={({ id: clientId }) => ({
                        className: 'cursor-pointer',
                        onClick(event) {
                            navigate({ to: CLIENT_DETAILS_ROUTE, params: { orgId, id: clientId } }, event);
                        },
                    })}
                />
            )}
        </RecordCard>
    );
}
